import * as React from "react";
import { useCallback, useState } from "react";
import { Button, Dialog, DialogFooter, DialogType, IDropdownOption, PrimaryButton } from "office-ui-fabric-react";
import { ApprovalType, GateDecision } from "../../../../entities/Subentities";
import { ApprovalDecisionType } from "../../../../store/ApprovalCenterStore";
import { GATE_DECISION_PAYLOAD_KEY, StageTransitionApprovalDetails } from "../../../../store/StageTransitionApprovalStore";
import TextInput from "../../../common/inputs/TextInput/TextInput";
import DropdownInput from "../../../common/inputs/DropdownInput";
import LabellableComponent from "../../../common/LabellableComponent";
import { optionalCommentValidator, requiredCommentValidator } from "../../common";
import { DecisionPopupProps } from "../../approvalTabBase";
import GenericCommentPopup from "../../GenericCommentPopup";

const GATE_DECISION_TOOLTIP = "Please select the decision for the gate.";

const APPROVE_OPTIONS: IDropdownOption[] = [
    { key: GateDecision.Go, text: "Go" },
    { key: GateDecision.ConditionalGo, text: "Conditional Go" }
];

const REJECT_OPTIONS: IDropdownOption[] = [
    { key: GateDecision.Recycle, text: "Recycle" },
    { key: GateDecision.Hold, text: "Hold" },
    { key: GateDecision.Kill, text: "Kill" }
];

const stageTransitionPopupContent = {
    approve: {
        title: "Approve Stage Transition",
        subText: "Are you sure you would like to approve the transition to the next stage?"
    },
    reject: {
        title: "Reject Stage Transition",
        subText: "Are you sure you would like to reject the transition to the next stage?"
    }
};

const StageTransitionApprovalPopup = (props: Readonly<DecisionPopupProps<StageTransitionApprovalDetails>>) => {
    const hasGateDecision = props.requests.some(r => r.details?.approvalForTransition === ApprovalType.GateDecision);

    if (hasGateDecision) {
        return <GateDecisionPopupBody {...props} />;
    }
    return <GenericCommentPopup {...props} content={stageTransitionPopupContent} />;
};

const GateDecisionPopupBody = ({
    initialDirection,
    onSubmit,
    onCancel
}: DecisionPopupProps<StageTransitionApprovalDetails>) => {
    const isApprove = initialDirection === ApprovalDecisionType.Approved;
    const options = isApprove ? APPROVE_OPTIONS : REJECT_OPTIONS;

    const [gateDecision, setGateDecision] = useState<GateDecision | undefined>(undefined);
    const [comment, setComment] = useState<string | undefined>();

    const validator = isApprove ? optionalCommentValidator : requiredCommentValidator;
    const isCommentValid = validator.isValid(comment);
    const isValid = gateDecision !== undefined && isCommentValid;

    const handleSubmit = useCallback(() => {
        if (!isValid || gateDecision === undefined) {
            return;
        }
        onSubmit(comment ?? "", { [GATE_DECISION_PAYLOAD_KEY]: gateDecision });
    }, [isValid, comment, gateDecision, onSubmit]);

    const title = isApprove ? "Approve Stage Transition" : "Reject Stage Transition";
    const subText = isApprove
        ? "Select the gate decision and confirm the approval of the transition to the next stage."
        : "Select the gate decision and provide a comment to reject the transition to the next stage.";
    const commentLabel = isApprove ? "Approval Comment" : "Rejection Comment";

    return (
        <Dialog
            hidden={false}
            onDismiss={onCancel}
            maxWidth={500}
            dialogContentProps={{
                type: DialogType.close,
                title,
                subText
            }}
            modalProps={{
                className: "approval-decision-popup"
            }}>
            <div className="panel-area ">
                <div className="grid-item">
                    <LabellableComponent
                        className="field-container"
                        label="Gate Decision"
                        description={GATE_DECISION_TOOLTIP}
                        required>
                        <DropdownInput
                            value={gateDecision}
                            inputProps={{ placeholder: "Select a decision", options }}
                            onChanged={value => setGateDecision(value as GateDecision)}
                        />
                    </LabellableComponent>
                </div>
                <div className="grid-item">
                    <LabellableComponent className="field-container" label={commentLabel} required={!isApprove}>
                        <TextInput
                            value={comment ?? ""}
                            onChanged={val => setComment(val)}
                            validator={validator}
                            inputProps={{
                                multiline: true,
                                required: !isApprove
                            }}
                        />
                    </LabellableComponent>
                </div>
            </div>
            <DialogFooter>
                <PrimaryButton
                    onClick={handleSubmit}
                    text={isApprove ? "Approve" : "Reject"}
                    disabled={!isValid}
                    className={isApprove ? undefined : "button-danger"}
                />
                <Button onClick={onCancel} text="Cancel" />
            </DialogFooter>
        </Dialog>
    );
};

export default StageTransitionApprovalPopup;
